import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LockSimple, Plus, SealWarning } from "@phosphor-icons/react";
import { fetcher, fmtN } from "./common";

const INVENTORY_KINDS = [
  { value: "fnp", label: "Charge à payer — facture non parvenue (4081)" },
  { value: "par", label: "Produit à recevoir — facture à établir (4181)" },
  { value: "cca", label: "Charge constatée d'avance (4761)" },
  { value: "pca", label: "Produit constaté d'avance (4771)" },
  { value: "provision", label: "Dotation aux provisions / dépréciations" },
];

function InventoryForm({ file, yearEnd, accounts, onSaved }) {
  const [form, setForm] = React.useState({
    kind: "fnp", date: yearEnd, label: "", amount: "", account_code: "",
    debit_account: "6914", credit_account: "491", tiers: "", extourne: true,
  });
  const [saving, setSaving] = React.useState(false);
  const isProvision = form.kind === "provision";

  // Préremplit la date avec la fin de l'exercice et la resynchronise à chaque changement d'exercice
  React.useEffect(() => {
    if (yearEnd) setForm((f) => ({ ...f, date: yearEnd }));
  }, [yearEnd]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post(`/accounting/files/${file.id}/inventory-entry`, {
        kind: form.kind, date: form.date, label: form.label, amount: parseFloat(form.amount),
        account_code: isProvision ? null : form.account_code.trim(),
        debit_account: isProvision ? form.debit_account.trim() : null,
        credit_account: isProvision ? form.credit_account.trim() : null,
        tiers: form.tiers || null, extourne: form.extourne,
      });
      toast.success(`Écriture d'inventaire ${r.data.entry.piece_no} enregistrée${r.data.extourne ? " (+ extourne)" : ""}`);
      if (r.data.warning) toast.warning(r.data.warning);
      setForm({ ...form, label: "", amount: "" });
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="border border-zinc-200 bg-white rounded-md p-5 space-y-3" data-testid="inventory-form">
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Écriture d'inventaire</div>
      <div>
        <Label>Type</Label>
        <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
          <SelectTrigger data-testid="inv-kind"><SelectValue /></SelectTrigger>
          <SelectContent>{INVENTORY_KINDS.map((k) => (<SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Date</Label><DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="inv-date" /></div>
        <div><Label>Montant</Label><Input type="number" min="0" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="inv-amount" /></div>
      </div>
      <div><Label>Libellé</Label><Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="inv-label" placeholder="Loyer décembre non parvenu…" /></div>
      {isProvision ? (
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Compte de dotation</Label><Input list="inv-acc" value={form.debit_account} onChange={(e) => setForm({ ...form, debit_account: e.target.value })} data-testid="inv-debit-acc" placeholder="6914" /></div>
          <div><Label>Compte de dépréciation</Label><Input list="inv-acc" value={form.credit_account} onChange={(e) => setForm({ ...form, credit_account: e.target.value })} data-testid="inv-credit-acc" placeholder="491" /></div>
        </div>
      ) : (
        <div>
          <Label>{form.kind === "par" || form.kind === "pca" ? "Compte de produit (7xx)" : "Compte de charge (6xx)"}</Label>
          <Input list="inv-acc" required value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} data-testid="inv-account" placeholder="601" />
        </div>
      )}
      <datalist id="inv-acc">{accounts.map((a) => (<option key={a.code} value={a.code}>{a.name}</option>))}</datalist>
      <div className="grid grid-cols-2 gap-3 items-end">
        <div><Label>Tiers</Label><Input value={form.tiers} onChange={(e) => setForm({ ...form, tiers: e.target.value })} placeholder="Optionnel" /></div>
        <label className="flex items-center gap-2 text-sm pb-1.5">
          <Checkbox checked={form.extourne} onCheckedChange={(v) => setForm({ ...form, extourne: !!v })} data-testid="inv-extourne" />
          <span>Extourne à l'ouverture</span>
        </label>
      </div>
      <Button type="submit" disabled={saving || !form.amount || !form.label} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="inv-submit">
        <Plus size={14} className="mr-1" /> {saving ? "Enregistrement…" : "Comptabiliser"}
      </Button>
    </form>
  );
}

/** Assistant de clôture guidée : écritures d'inventaire, contrôles, détermination du résultat, report à nouveau. */
export default function Cloture({ file, years }) {
  // Ordre chronologique : la clôture porte par défaut sur l'exercice ouvert le plus ANCIEN
  const openYears = (years || []).filter((y) => y.status === "ouvert").slice().sort((a, b) => (a.start < b.start ? -1 : 1));
  const [yearId, setYearId] = React.useState("");
  const effectiveYearId = yearId || openYears[0]?.id || "";
  const { data: check, mutate: recheck } = useSWR(
    effectiveYearId ? `/accounting/files/${file.id}/years/${effectiveYearId}/precheck` : null, fetcher);
  const { data: accounts = [] } = useSWR(`/accounting/files/${file.id}/accounts`, fetcher);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [closing, setClosing] = React.useState(false);
  const year = check?.year;

  if (file.chart_type !== "syscohada") {
    return <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center text-sm text-zinc-500" data-testid="cloture-unavailable">La clôture guidée est disponible pour les dossiers en plan SYSCOHADA.</div>;
  }
  if (!openYears.length) {
    return <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center text-sm text-zinc-500" data-testid="cloture-no-year">Aucun exercice ouvert à clôturer.</div>;
  }

  const doClose = async () => {
    setClosing(true);
    try {
      const r = await api.post(`/accounting/files/${file.id}/years/${effectiveYearId}/cloture`, { report_a_nouveau: true });
      toast.success(`Exercice clôturé — résultat ${fmtN(r.data.resultat)} ${file.currency}${r.data.an_entry ? " — à-nouveaux générés" : ""}`);
      if (r.data.warning) toast.warning(r.data.warning);
      setConfirmOpen(false);
      setYearId("");  // rebascule sur le plus ancien exercice encore ouvert
      mutate(`/accounting/files/${file.id}/years`);
      recheck();
    } catch (err) { toast.error(err?.response?.data?.detail || "Clôture impossible"); }
    finally { setClosing(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div>
          <Label>Exercice à clôturer</Label>
          <Select value={effectiveYearId} onValueChange={setYearId}>
            <SelectTrigger className="w-72" data-testid="cloture-year"><SelectValue /></SelectTrigger>
            <SelectContent>{openYears.map((y) => (<SelectItem key={y.id} value={y.id}>{y.label} ({y.start} → {y.end})</SelectItem>))}</SelectContent>
          </Select>
        </div>

        {check && (
          <div className="border border-zinc-200 bg-white rounded-md p-5 space-y-3" data-testid="cloture-precheck">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Contrôles avant clôture</div>
            <div className="flex items-center justify-between text-sm">
              <span>Écritures en brouillard</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${check.brouillards ? "bg-red-50 text-red-800" : "bg-emerald-50 text-emerald-800"}`} data-testid="cloture-drafts">
                {check.brouillards ? `${check.brouillards} à traiter` : "Aucune"}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Solde du compte d'attente (471)</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${Math.abs(check.solde_compte_attente) >= 0.01 ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`} data-testid="cloture-attente">
                {fmtN(check.solde_compte_attente)} {file.currency}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Résultat estimé de l'exercice</span>
              <span className="font-medium" data-testid="cloture-resultat">{fmtN(check.resultat_estime)} {file.currency}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Exercice suivant (report à nouveau)</span>
              <span className="text-xs text-zinc-500" data-testid="cloture-next-year">{check.next_year ? check.next_year.label : "Non créé — le report sera ignoré"}</span>
            </div>
            {check.earlier_open && (
              <div className="text-xs text-red-700 flex items-center gap-1" data-testid="cloture-earlier-open">
                <SealWarning size={14} /> L'exercice antérieur « {check.earlier_open.label} » est encore ouvert — clôturez-le d'abord.
              </div>
            )}
            <div className="border-t border-zinc-100 pt-3">
              <Button onClick={() => setConfirmOpen(true)} disabled={!check.closable} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="cloture-run">
                <LockSimple size={14} className="mr-1" /> Clôturer {year?.label}
              </Button>
              {!check.closable && (check.brouillards > 0 || check.earlier_open) && (
                <p className="text-xs text-red-700 mt-2 flex items-center gap-1"><SealWarning size={14} /> {check.brouillards > 0 ? "Validez ou supprimez les brouillards avant de clôturer." : "Un exercice antérieur est encore ouvert."}</p>
              )}
            </div>
          </div>
        )}

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">Confirmer la clôture</DialogTitle>
              <DialogDescription>
                La clôture fige l'exercice : écriture de détermination du résultat ({fmtN(check?.resultat_estime || 0)} {file.currency}) au journal OD,
                puis report à nouveau des soldes de bilan dans l'exercice suivant. Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annuler</Button>
              <Button onClick={doClose} disabled={closing} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="cloture-confirm">
                {closing ? "Clôture en cours…" : "Confirmer la clôture"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <InventoryForm file={file} yearEnd={year?.end || ""} accounts={accounts} onSaved={recheck} />
    </div>
  );
}
